import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { API, useAuth } from "../App";
import { ArrowLeft, Heart, Zap, Brain, Loader2, Lock, Crown, TrendingUp, TrendingDown, Minus } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";
import { useIsWebView } from "../hooks/useIsWebView";

const SliderInput = ({ label, value, onChange, labels, colors, icon: Icon, testId }) => (
  <div className="mb-6" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-white/50" />
        <span className="text-sm font-medium text-white/70">{label}</span>
      </div>
      <span className="text-sm font-semibold" style={{ color: colors[value - 1] }}>{labels[value - 1]}</span>
    </div>
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`flex-1 h-10 rounded-xl text-sm font-medium transition-all ${
            v === value
              ? "ring-2 ring-offset-2 ring-offset-[#0a0a0f] text-white scale-105"
              : "bg-white/5 text-white/40 hover:bg-white/10"
          }`}
          style={v === value ? { backgroundColor: colors[v - 1], ringColor: colors[v - 1] } : {}}
          data-testid={`${testId}-${v}`}
        >
          {v}
        </button>
      ))}
    </div>
  </div>
);

const DayDot = ({ log, t }) => {
  const moodColors = ["#EF4444", "#F97316", "#6B7280", "#10B981", "#002FA7"];
  const day = new Date(log.created_at).toLocaleDateString("es-ES", { weekday: "short" }).slice(0, 2);
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-semibold"
        style={{ backgroundColor: moodColors[log.mood - 1] }}
        title={`${t("mood_label")}: ${log.mood}/5`}
      >
        {log.mood}
      </div>
      <span className="text-[10px] text-white/40 uppercase">{day}</span>
    </div>
  );
};

const MoodTracker = () => {
  const navigate = useNavigate();
  const isWebView = useIsWebView();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [mood, setMood] = useState(3);
  const [energy, setEnergy] = useState(3);
  const [stress, setStress] = useState(3);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [submittedToday, setSubmittedToday] = useState(false);
  const [history, setHistory] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const plan = user?.plan || "descubre";
  const hasAccess = true; // All plans now have daily emotional check-in

  useEffect(() => {
    if (!hasAccess) { setLoadingData(false); return; }
    let cancelled = false;

    const fetchCoreData = async () => {
      try {
        const [historyRes, summaryRes] = await Promise.all([
          axios.get(`${API}/mood/history`, { withCredentials: true, timeout: 8000 }),
          axios.get(`${API}/mood/weekly-summary`, { withCredentials: true, timeout: 8000 })
        ]);
        if (cancelled) return;
        setHistory(historyRes.data);
        setWeeklySummary(summaryRes.data);
        const today = new Date().toISOString().slice(0, 10);
        const todayEntry = historyRes.data.find(l => l.created_at?.startsWith(today));
        if (todayEntry) {
          setSubmittedToday(true);
          setMood(todayEntry.mood);
          setEnergy(todayEntry.energy);
          setStress(todayEntry.stress);
        }
      } catch (e) { console.error("Mood data fetch failed:", e); }
      finally { if (!cancelled) setLoadingData(false); }
    };

    // Fire-and-forget: fetch AI insight in background without blocking the UI.
    const fetchInsight = async () => {
      setInsightLoading(true);
      try {
        const res = await axios.get(`${API}/mood/weekly-insight`, { withCredentials: true, timeout: 30000 });
        if (cancelled) return;
        if (res.data?.insight) {
          setWeeklySummary(prev => prev ? { ...prev, insight: res.data.insight } : { insight: res.data.insight });
        }
      } catch (e) { console.debug("Mood insight fetch failed:", e.message); }
      finally { if (!cancelled) setInsightLoading(false); }
    };

    fetchCoreData();
    fetchInsight();

    return () => { cancelled = true; };
  }, [hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  const handleCheckin = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/mood/checkin`, { mood, energy, stress, note }, { withCredentials: true });
      setSubmittedToday(true);
      setNote("");
      // Refresh core data (fast). Insight will auto-update on next visit (cached).
      const [historyRes, summaryRes] = await Promise.all([
        axios.get(`${API}/mood/history`, { withCredentials: true, timeout: 8000 }),
        axios.get(`${API}/mood/weekly-summary`, { withCredentials: true, timeout: 8000 })
      ]);
      setHistory(historyRes.data);
      setWeeklySummary(prev => ({ ...summaryRes.data, insight: prev?.insight ?? summaryRes.data.insight }));
    } catch (error) { console.error("Mood checkin failed:", error); }
    finally { setLoading(false); }
  };

  const moodLabels = [t("mood_1"), t("mood_2"), t("mood_3"), t("mood_4"), t("mood_5")];
  const energyLabels = [t("energy_1"), t("energy_2"), t("energy_3"), t("energy_4"), t("energy_5")];
  const stressLabels = [t("stress_1"), t("stress_2"), t("stress_3"), t("stress_4"), t("stress_5")];
  const moodColors = ["#EF4444", "#F97316", "#6B7280", "#10B981", "#002FA7"];
  const energyColors = ["#6B7280", "#F59E0B", "#10B981", "#002FA7", "#5B21B6"];
  const stressColors = ["#10B981", "#6B7280", "#F59E0B", "#F97316", "#EF4444"];

  // Gate
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="mood-gate">
        <header className="relative z-20 border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white"><ArrowLeft className="w-5 h-5" /><span className="font-medium">{t("nav_back")}</span></Link>
            <div className="flex items-center gap-3">
              <LanguageSelector variant="dark" />
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1"><FuturaIcon className="w-full h-full text-white" /></div>
            </div>
          </div>
        </header>
        <main className="relative z-10 max-w-lg mx-auto px-6 py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6"><Lock className="w-10 h-10 text-white/30" /></div>
          <h1 className="font-['Outfit'] text-2xl font-semibold mb-3">{t("mood_gate_title")}</h1>
          <p className="text-white/50 mb-8">{t("mood_gate_desc")}</p>
          {!isWebView && (
            <Button onClick={() => navigate("/pricing")} className="bg-gradient-to-r from-[#002FA7] to-[#5B21B6] hover:opacity-90 text-white rounded-full px-8 py-3" data-testid="mood-gate-upgrade-btn">
              <Crown className="w-4 h-4 mr-2" />{t("mood_gate_btn")}
            </Button>
          )}
          {isWebView && (
            <p className="text-white/40 text-sm" data-testid="webview-sub-text-mood">{t("webview_sub_managed")}</p>
          )}
        </main>
      </div>
    );
  }

  const summary = weeklySummary?.summary;
  const weeklyLogs = weeklySummary?.logs || [];

  const getTrend = (current, label) => {
    if (!summary) return null;
    const avg = summary[label];
    if (current > avg + 0.3) return <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />;
    if (current < avg - 0.3) return <TrendingDown className="w-3.5 h-3.5 text-red-400" />;
    return <Minus className="w-3.5 h-3.5 text-white/30" />;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="mood-tracker-page">
      <header className="relative z-20 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white"><ArrowLeft className="w-5 h-5" /><span className="font-medium">{t("nav_back")}</span></Link>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="dark" />
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1"><FuturaIcon className="w-full h-full text-white" /></div>
            <span className="font-['Outfit'] font-semibold text-lg">FUTURA</span>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold tracking-tight mb-2">{t("mood_title")}</h1>
          <p className="text-white/50">{t("mood_subtitle")}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Check-in card */}
          <div className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8" data-testid="checkin-card">
            <h2 className="font-['Outfit'] text-lg font-semibold mb-1">{submittedToday ? t("mood_today_done") : t("mood_checkin_title")}</h2>
            <p className="text-white/40 text-sm mb-6">{submittedToday ? t("mood_today_done_sub") : t("mood_checkin_sub")}</p>

            <SliderInput label={t("mood_label")} value={mood} onChange={setMood} labels={moodLabels} colors={moodColors} icon={Heart} testId="mood-slider" />
            <SliderInput label={t("energy_label")} value={energy} onChange={setEnergy} labels={energyLabels} colors={energyColors} icon={Zap} testId="energy-slider" />
            <SliderInput label={t("stress_label")} value={stress} onChange={setStress} labels={stressLabels} colors={stressColors} icon={Brain} testId="stress-slider" />

            {!submittedToday && (
              <>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("mood_note_placeholder")}
                  className="w-full min-h-[60px] bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 resize-none mb-4"
                  data-testid="mood-note-input"
                />
                <Button
                  onClick={handleCheckin}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:opacity-90 text-white rounded-full py-3 font-medium"
                  data-testid="mood-submit-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("mood_save")}
                </Button>
              </>
            )}
          </div>

          {/* Weekly summary */}
          <div className="space-y-6">
            {/* Weekly chart */}
            {weeklyLogs.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6" data-testid="weekly-chart">
                <h3 className="font-['Outfit'] text-lg font-semibold mb-4">{t("mood_weekly_title")}</h3>
                <div className="flex items-end justify-between gap-2">
                  {weeklyLogs.slice(-7).map((log) => (
                    <DayDot key={log.checkin_id} log={log} t={t} />
                  ))}
                </div>
              </div>
            )}

            {/* Averages */}
            {summary && (
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6" data-testid="weekly-averages">
                <h3 className="font-['Outfit'] text-lg font-semibold mb-4">{t("mood_averages_title")}</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#10B981]">{summary.avg_mood}</div>
                    <p className="text-xs text-white/40 mt-1">{t("mood_label")}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#002FA7]">{summary.avg_energy}</div>
                    <p className="text-xs text-white/40 mt-1">{t("energy_label")}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#F59E0B]">{summary.avg_stress}</div>
                    <p className="text-xs text-white/40 mt-1">{t("stress_label")}</p>
                  </div>
                </div>
                <p className="text-xs text-white/30 text-center mt-3">{summary.total_checkins} {t("mood_checkins_count")}</p>
              </div>
            )}

            {/* AI Insight */}
            {(weeklySummary?.insight || insightLoading) && (
              <div className="bg-gradient-to-br from-[#002FA7]/10 to-[#5B21B6]/10 border border-[#002FA7]/20 rounded-3xl p-6" data-testid="mood-insight">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-5 h-5 text-[#002FA7]" />
                  <h3 className="font-['Outfit'] font-semibold">{t("mood_insight_title")}</h3>
                </div>
                {weeklySummary?.insight ? (
                  <p className="text-white/70 text-sm leading-relaxed">{weeklySummary.insight}</p>
                ) : (
                  <div className="flex items-center gap-2" data-testid="mood-insight-loading">
                    <Loader2 className="w-4 h-4 animate-spin text-[#002FA7]" />
                    <p className="text-white/50 text-sm">{t("mood_insight_loading")}</p>
                  </div>
                )}
              </div>
            )}

            {/* Empty state */}
            {!loadingData && weeklyLogs.length === 0 && !submittedToday && (
              <div className="bg-white/5 border border-white/10 rounded-3xl p-8 text-center" data-testid="mood-empty">
                <Heart className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50 text-sm">{t("mood_empty")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent history */}
        {history.length > 0 && (
          <div className="mt-8">
            <h3 className="font-['Outfit'] text-lg font-semibold mb-4">{t("mood_history_title")}</h3>
            <div className="space-y-3">
              {history.slice(0, 10).map((log, i) => (
                <div key={log.checkin_id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4" data-testid={`mood-log-${i}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: moodColors[log.mood - 1] }}>
                    {log.mood}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-white/60"><Zap className="w-3 h-3 inline mr-1" />{log.energy}/5</span>
                      <span className="text-white/60"><Brain className="w-3 h-3 inline mr-1" />{log.stress}/5</span>
                    </div>
                    {log.note && <p className="text-white/40 text-xs mt-1 truncate">{log.note}</p>}
                  </div>
                  <span className="text-xs text-white/30 flex-shrink-0">
                    {new Date(log.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-white/20 text-xs">{t("mood_disclaimer")}</p>
        </div>
      </main>
    </div>
  );
};

export default MoodTracker;
